import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, Input, Modal, Space, Switch, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import {
  useCreateStateMutation,
  useDeleteStateMutation,
  useGetStatesQuery,
  useImportStatesMutation,
  useUpdateStateMutation,
} from '@/modules/locations/locationsApi';
import type { CreateStatePayload, StateMaster } from '@/types/location';
import { resolveMutationResult } from '@/utils/apiResponse';
import { confirmPermanentLocationDelete } from '@/utils/confirmDialog';
import { formatDateTime } from '@/utils/date';
import { useLocationBulkUpload } from './components/useLocationBulkUpload';
import styles from './Locations.module.scss';

type StateFormValues = CreateStatePayload;

export default function States() {
  const notify = useNotify();
  const [form] = Form.useForm<StateFormValues>();
  const { data, isLoading, isFetching, refetch } = useGetStatesQuery();
  const [createState, { isLoading: isCreating }] = useCreateStateMutation();
  const [updateState, { isLoading: isUpdating }] = useUpdateStateMutation();
  const [deleteState, { isLoading: isDeleting }] = useDeleteStateMutation();
  const [importStates, { isLoading: isImporting }] = useImportStatesMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingState, setEditingState] = useState<StateMaster | null>(null);
  const { bulkUploadControls } = useLocationBulkUpload({
    entityLabel: 'State',
    entityLabelPlural: 'States',
    exportPath: '/states/export',
    templatePath: '/states/export-template',
    exportFilename: 'states.xlsx',
    templateFilename: 'states-template.xlsx',
    importMutation: importStates,
    isImporting,
    onImported: () => {
      void refetch();
    },
  });

  useGlobalLoader(isFetching && !isLoading);

  const states = data?.results ?? [];

  useEffect(() => {
    if (!modalOpen) return;
    form.setFieldsValue({
      name: editingState?.name ?? '',
      isActive: editingState?.isActive ?? true,
    });
  }, [editingState, form, modalOpen]);

  const openCreate = () => {
    setEditingState(null);
    setModalOpen(true);
  };

  const openEdit = (state: StateMaster) => {
    setEditingState(state);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingState(null);
    form.resetFields();
  };

  const handleSubmit = async (values: StateFormValues) => {
    const body = { ...values, name: values.name.trim() };
    const result = await resolveMutationResult(
      editingState
        ? updateState({ id: editingState.id, body })
        : createState(body),
    );

    if (result.ok) {
      notify.success(result.message ?? (editingState ? 'State updated.' : 'State created.'));
      closeModal();
      return;
    }

    notify.error(result.message);
  };

  const handleDelete = async (state: StateMaster) => {
    const confirmed = await confirmPermanentLocationDelete(
      state.name,
      'All districts, constituencies, and assemblies under this state',
    );
    if (!confirmed) return;

    const result = await resolveMutationResult(deleteState(state.id));
    if (result.ok) {
      notify.success(result.message ?? 'State deleted.');
      return;
    }

    notify.error(result.message);
  };

  const columns: ColumnsType<StateMaster> = [
    {
      title: 'STATE',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record) => (
        <div>
          <div className={styles.nameCell}>{name}</div>
          <div className={styles.subText}>{record.slug}</div>
        </div>
      ),
    },
    {
      title: 'STATUS',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 140,
      render: (isActive: boolean) => (
        <span className={isActive ? styles.statusActive : styles.statusInactive}>
          {isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      title: 'UPDATED',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      render: (value: string) => formatDateTime(value),
    },
    {
      title: 'ACTION',
      key: 'action',
      width: 140,
      align: 'center',
      render: (_value, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
            loading={isDeleting}
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          <>
            {bulkUploadControls}
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Add state
            </Button>
          </>
        }
      />

      <div className={styles.tableCard}>
        <Table<StateMaster>
          rowKey="id"
          size="small"
          loading={isLoading}
          columns={columns}
          dataSource={states}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
          }}
        />
      </div>

      <Modal
        title={editingState ? 'Edit State' : 'Add State'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => form.submit()}
        confirmLoading={isCreating || isUpdating}
        destroyOnClose
      >
        <Form<StateFormValues>
          form={form}
          layout="vertical"
          initialValues={{ isActive: true }}
          onFinish={handleSubmit}
        >
          <Form.Item
            label="State"
            name="name"
            rules={[{ required: true, message: 'Please enter state name.' }]}
          >
            <Input placeholder="Enter state name" />
          </Form.Item>
          <Form.Item label="Active" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
